import React, { useState, useEffect } from 'react';
import { apiClient, useAuth } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Sparkles, MessageCircle, Users, TrendingUp, Search, Eye,
  Download, BarChart3, Clock, ArrowUpRight, Shield
} from 'lucide-react';

export default function ClaretAdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [chatDialog, setChatDialog] = useState({ open: false, userId: null, userName: '', chats: [], loading: false });
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchStats(); }, []);

  async function fetchStats() {
    setLoading(true);
    try {
      const res = await apiClient.get('/security/claret-stats?days=30');
      setStats(res.data);
    } catch {
      toast.error('Failed to load Claret stats');
    } finally {
      setLoading(false);
    }
  }

  async function viewUserChats(userId, userName) {
    setChatDialog({ open: true, userId, userName, chats: [], loading: true });
    try {
      const res = await apiClient.get(`/security/claret-chats?user_id=${userId}&days=30`);
      setChatDialog(prev => ({ ...prev, chats: res.data?.chats || [], loading: false }));
    } catch {
      setChatDialog(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load chat history');
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const res = await apiClient.get('/security/claret-chats/export?days=30');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claret-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data?.total_messages || 0} messages`);
    } catch {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  }

  const filteredUsers = (stats?.user_stats || []).filter(u =>
    !search || (u.user_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.department || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="claret-admin-page">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="claret-admin-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Claret Intelligence</h1>
            <p className="text-muted-foreground text-sm">AI assistant usage analytics — Super Admin only (last 30 days)</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-claret-btn">
          <Download className="h-4 w-4 mr-2" />
          {exporting ? 'Exporting...' : 'Backup / Export'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <MessageCircle className="h-6 w-6 mx-auto mb-2 text-indigo-500" />
            <p className="text-3xl font-bold">{stats?.total_messages || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Messages</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <Users className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
            <p className="text-3xl font-bold">{stats?.active_users || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Active Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <BarChart3 className="h-6 w-6 mx-auto mb-2 text-blue-500" />
            <p className="text-3xl font-bold">{stats?.active_users ? Math.round((stats?.total_messages || 0) / stats.active_users) : 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Avg Msgs/User</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <TrendingUp className="h-6 w-6 mx-auto mb-2 text-orange-500" />
            <p className="text-3xl font-bold">{(stats?.daily_trend || []).length}</p>
            <p className="text-xs text-muted-foreground mt-1">Active Days</p>
          </CardContent>
        </Card>
      </div>

      {/* Trending Topics */}
      {(stats?.trending_topics || []).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-500" /> Trending Topics</CardTitle>
            <CardDescription>Most discussed topics in Claret conversations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(stats?.trending_topics || []).map((t, i) => (
                <Badge key={i} variant="outline" className="text-sm px-3 py-1" data-testid={`topic-${i}`}>
                  {t.topic} <span className="ml-1.5 text-muted-foreground text-xs">({t.count})</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Stats Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Usage by Employee</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search user or dept..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="claret-user-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-center">Messages</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No Claret usage found</TableCell></TableRow>
              ) : filteredUsers.map((u, i) => (
                <TableRow key={u.user_id} data-testid={`claret-user-row-${i}`}>
                  <TableCell className="font-bold text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">{u.user_name || 'Unknown'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{(u.role || '').replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.department || '-'}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={u.message_count > 50 ? 'bg-indigo-500/10 text-indigo-600' : u.message_count > 20 ? 'bg-blue-500/10 text-blue-600' : 'bg-muted text-foreground'}>
                      {u.message_count}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.last_active ? new Date(u.last_active).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => viewUserChats(u.user_id, u.user_name)} data-testid={`view-chats-${i}`}>
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Chat History Dialog */}
      <Dialog open={chatDialog.open} onOpenChange={o => { if (!o) setChatDialog(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="claret-chat-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-500" />
              {chatDialog.userName} — Claret History
            </DialogTitle>
            <DialogDescription>Last 30 days of Claret conversations</DialogDescription>
          </DialogHeader>
          {chatDialog.loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : chatDialog.chats.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">No messages found in the last 30 days.</div>
          ) : (
            <ScrollArea className="flex-1 max-h-[60vh] pr-3">
              <div className="space-y-3 py-2">
                {chatDialog.chats.map((msg, i) => {
                  const isUser = msg.role === 'user';
                  return (
                    <div key={msg.id || i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                        isUser ? 'bg-indigo-500 text-white' : 'bg-muted'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.message || msg.content || ''}</p>
                        <p className={`text-[10px] mt-1 ${isUser ? 'text-indigo-200' : 'text-muted-foreground'}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
